import { Box } from "@mui/material";
import { Suspense, lazy } from "react";
import { Outlet, type RouteObject } from "react-router";
import LoadingScreen from "./components/LoadingScreen";

const Loadable = (Component) => (props) =>
  (
    <Suspense fallback={<LoadingScreen />}>
      <Component {...props} />
    </Suspense>
  );

// Contexts
import { CountdownProvider } from "src/contexts/v2/CountdownContext";
import { OrderBookProvider } from "./contexts/OrderBookContext";

// Guards
const AuthGuard = Loadable(lazy(() => import("./components/AuthGuard")));
const CountdownGuard = Loadable(
  lazy(() => import("./components/CountdownGuard"))
);
const GuestGuard = Loadable(lazy(() => import("./components/GuestGuard")));

// Dashboard Layout
const DashboardLayout = Loadable(
  lazy(() => import("./components/dashboard/DashboardLayout"))
);

// Shared Layout
const Layout = Loadable(lazy(() => import("./components/shared/Layout")));

// Trade Components
const OperationsTab = Loadable(
  lazy(() => import("./components/trade/OperationsTab"))
);
const TradeOrder = Loadable(
  lazy(() => import("./components/trade/TradeOrder"))
);

// Home Components
const SupportPage = Loadable(
  lazy(() => import("src/components/home/SupportPage"))
);

// Home Footer Components
const Community = Loadable(
  lazy(() => import("src/components/home/Footer/Community"))
);
const CriptoMarket = Loadable(
  lazy(() => import("src/components/home/Footer/CriptoMarket"))
);
const OptionsMarket = Loadable(
  lazy(() => import("src/components/home/Footer/OptionsMarket"))
);
const WhoWeAre = Loadable(
  lazy(() => import("src/components/home/Footer/WhoWeAre"))
);
const WhyChoooseUs = Loadable(
  lazy(() => import("src/components/home/Footer/WhyChoooseUs"))
);

// Dashboard Components
const DepositScreenWrapper = Loadable(
  lazy(() => import("src/components/dashboard/deposit/DepositScreenWrapper"))
);
const SecurityWrapper = Loadable(
  lazy(() => import("src/components/dashboard/profile/SecurityWrapper"))
);
const WithdrawScreenWrapper = Loadable(
  lazy(() => import("src/components/dashboard/withdraw/WithdrawScreenWrapper"))
);

// Order Components
const OrdersCenterSpot = Loadable(
  lazy(() => import("src/pages/dashboard/OrdersCenter/OrdersCenterSpot"))
);
const OrdersCenterTrade = Loadable(
  lazy(() => import("src/pages/dashboard/OrdersCenter/OrdersCenterTrade"))
);

// Profile Page
const ProfilePage = Loadable(
  lazy(() => import("src/pages/dashboard/Profile/page"))
);

// Authentication pages
const Login = Loadable(lazy(() => import("./pages/authentication/Login")));
const PasswordRecovery = Loadable(
  lazy(() => import("./pages/authentication/PasswordRecovery"))
);
const PasswordReset = Loadable(
  lazy(() => import("./pages/authentication/PasswordReset"))
);
const Register = Loadable(
  lazy(() => import("./pages/authentication/Register"))
);

const ConfirmationEmailCode = Loadable(
  lazy(() => import("./pages/authentication/ConfirmationEmailCode"))
);

const VerifyCode = Loadable(
  lazy(() => import("./pages/authentication/VerifyCode"))
);
const WaitingList = Loadable(
  lazy(() => import("./pages/authentication/WaitingList"))
);

// Dashboard pages
const Profile = Loadable(lazy(() => import("./pages/dashboard/Profile")));
const Deposit = Loadable(lazy(() => import("./pages/dashboard/Deposit")));
const Overview = Loadable(lazy(() => import("./pages/dashboard/Overview")));
const BalanceHistory = Loadable(
  lazy(() => import("./pages/dashboard/BalanceHistory"))
);
const OperationsHistory = Loadable(
  lazy(() => import("./pages/dashboard/OperationsHistory"))
);

// Error pages
const AuthorizationRequired = Loadable(
  lazy(() => import("./pages/AuthorizationRequired"))
);
const NotFound = Loadable(lazy(() => import("./pages/NotFound")));
const ServerError = Loadable(lazy(() => import("./pages/ServerError")));

// Other pages
const Home = Loadable(lazy(() => import("./pages/Home")));

const TraderoomProviders = Loadable(
  lazy(() => import("./components/Stomp/TraderoomProviders"))
);

const routes: RouteObject[] = [
  {
    path: "authentication",
    children: [
      {
        path: "login",
        element: (
          <GuestGuard>
            <Login />
          </GuestGuard>
        ),
      },
      // {
      //   path: "login-unguarded",
      //   element: <Login />,
      // },
      {
        path: "password-recovery",
        element: <PasswordRecovery />,
      },
      {
        path: "password-reset",
        element: <PasswordReset />,
      },
      {
        path: "register",
        element: (
          <GuestGuard>
            <Register />
          </GuestGuard>
        ),
      },
      {
        path: "register-unguarded",
        element: <Register />,
      },
      {
        path: "verify-code",
        element: <VerifyCode />,
      },
      {
        path: "waiting_list",
        element: <WaitingList />,
      },
    ],
  },
  {
    path: "traderoom",
    element: <TraderoomProviders />,
    children: [
      {
        path: "",
        element: (
          <OrderBookProvider>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                flex: 1,
              }}
            >
              <Overview />
            </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                maxWidth: "320px",
                height: "100%",
                // maxHeight: "calc(100vh - 80px)",
                overflow: "scroll",
                gap: 1.5,
                px: 2,
                pb: 2.5,
                pt: 1,
                flex: 1,
                "@media (max-width: 1200px)": {
                  overflow: "initial",
                  flexDirection: "column",
                  maxWidth: "initial",
                  padding: "0 1rem",
                },
              }}
            >
              <CountdownProvider>
                <TradeOrder />
                <OperationsTab />
              </CountdownProvider>
            </Box>
          </OrderBookProvider>
        ),
      },
    ],
  },
  {
    path: "dashboard",
    element: (
      <CountdownGuard>
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      </CountdownGuard>
    ),
    children: [
      {
        path: "",
        element: <NotFound />,
      },
      {
        path: "profile",
        element: <Profile />,
        children: [
          {
            path: "",
            element: <ProfilePage />,
          },
          {
            path: "to_withdraw",
            element: <WithdrawScreenWrapper />,
          },
          {
            path: "deposit",
            element: <DepositScreenWrapper />,
          },
          {
            path: "security",
            element: <SecurityWrapper />,
          },
          { path: "support", element: <SupportPage /> },
          { path: "orders_center_trade", element: <OrdersCenterTrade /> },
          { path: "orders_center_spot", element: <OrdersCenterSpot /> },
        ],
      },
      {
        path: "deposit",
        element: <Deposit />,
      },
      {
        path: "history/balance",
        element: <BalanceHistory />,
      },
    ],
  },
  {
    path: "*",
    element: <Outlet />,
    children: [
      ...[
        "",
        "login",
        "register",
        "password-recovery",
        "password-recovery/code",
        "registration-success",
        "email-confirmation",
        "2fa",
        "reset-password",
      ].map((page) => ({
        path: page,
        element: (
          <GuestGuard>
            <Home />
          </GuestGuard>
        ),
      })),
      {
        path: "who-we-are",
        element: (
          <Layout>
            <WhoWeAre />
          </Layout>
        ),
      },
      {
        path: "options-market",
        element: (
          <Layout>
            <OptionsMarket />
          </Layout>
        ),
      },
      {
        path: "cripto-market",
        element: (
          <Layout>
            <CriptoMarket />
          </Layout>
        ),
      },
      {
        path: "community",
        element: (
          <Layout>
            <Community />
          </Layout>
        ),
      },
      {
        path: "why-choose-us",
        element: (
          <Layout>
            <WhyChoooseUs />
          </Layout>
        ),
      },
      // {
      //   path: "",
      //   element: (
      //     <CountdownGuard>
      //       <GuestGuard>
      //         <Login />
      //       </GuestGuard>
      //     </CountdownGuard>
      //   ),
      // },
      // {
      //   path: "login",
      //   element: (
      //     <CountdownGuard>
      //       <GuestGuard>
      //         <Login />
      //       </GuestGuard>
      //     </CountdownGuard>
      //   ),
      // },
      // {
      //   path: "register",
      //   element: (
      //     <CountdownGuard>
      //       <GuestGuard>
      //         <Register />
      //       </GuestGuard>
      //     </CountdownGuard>
      //   ),
      // },
      {
        path: "confirmation-email",
        element: (
          <CountdownGuard>
            <GuestGuard>
              <ConfirmationEmailCode />
            </GuestGuard>
          </CountdownGuard>
        ),
      },
      // {
      //   path: "confirmation-2f-code",
      //   element: (
      //     <CountdownGuard>
      //       <GuestGuard>
      //         <Confirmation2FCode />
      //       </GuestGuard>
      //     </CountdownGuard>
      //   ),
      // },
      // {
      //   path: "verify-code",
      //   element: (
      //     <CountdownGuard>
      //       <GuestGuard>
      //         <VerifyCode />
      //       </GuestGuard>
      //     </CountdownGuard>
      //   ),
      // },
      // {
      //   path: "password-recovery",
      //   element: (
      //     <CountdownGuard>
      //       <PasswordRecovery />
      //     </CountdownGuard>
      //   ),
      // },
      // {
      //   path: "password-reset",
      //   element: (
      //     <CountdownGuard>
      //       <PasswordReset />
      //     </CountdownGuard>
      //   ),
      // },
      // {
      //   path: "change-password",
      //   element: (
      //     <CountdownGuard>
      //       <ChangePassword />
      //     </CountdownGuard>
      //   ),
      // },

      {
        path: "401",
        element: (
          <CountdownGuard>
            <AuthorizationRequired />
          </CountdownGuard>
        ),
      },
      {
        path: "404",
        element: (
          <CountdownGuard>
            <NotFound />
          </CountdownGuard>
        ),
      },
      {
        path: "500",
        element: (
          <CountdownGuard>
            <ServerError />
          </CountdownGuard>
        ),
      },
      {
        path: "*",
        element: (
          <CountdownGuard>
            <NotFound />
          </CountdownGuard>
        ),
      },
    ],
  },
];

export default routes;
